import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Field, TextArea } from '@/components/forge/Field'
import { ForgeButton } from '@/components/forge/ForgeButton'
import { ForgeDialog } from '@/components/forge/ForgeDialog'
import { ForgeSelect } from '@/components/forge/ForgeSelect'
import { PlaceField } from '@/components/forge/PlaceField'
import { LinkField } from '@/components/forge/LinkField'
import { todayISO } from '@/features/logbook/api'
import { LINKS_MAX, NOTES_MAX_LENGTH } from '@/features/shiftHandovers/api'
import { placeOf, trioOf } from '@/features/hierarchy/place'

/**
 * File or edit a shift handover. LIVE — every field `ShiftHandoverCreate` and
 * `ShiftHandoverUpdate` take, and nothing they do not.
 *
 * ── Create-only fields are locked on an edit ─────────────────────────────
 * The place, the shift and the date are fixed once a handover exists. The edit
 * shows them, disabled, rather than hiding them: they are what the notes are
 * ABOUT, and a form that shows only the notes invites the reader to wonder.
 *
 * ── The shift list is the reader's own roster ────────────────────────────
 * The server accepts only a shift the creator is rostered on for that date, so
 * the honest list is `/auth/me`'s `shifts` — not Shift Master's whole catalogue,
 * which is behind `shifts.manage` and would offer shifts the save then refuses.
 * A reader who does hold `shifts.manage` (root among them, who is exempt from
 * the roster rule) is offered the catalogue instead. The page decides which.
 *
 * ── Links are chosen in a dialog, at exactly this place ──────────────────
 * See `LinkField`. Each list is behind the grant the server checks
 * for a NEW link; one the reader cannot open is stated, not drawn empty. On an
 * edit, links the handover already carries are kept whatever the grant —
 * the server re-checks only new ones, and dropping them silently would be a
 * save that removes what the reader could not see.
 *
 * ── Server refusals land on the control they name ────────────────────────
 * The API answers field-keyed (`shift_id`, `plant_id`, `work_order_ids`, …).
 * Those reasons are shown beside the control; anything else becomes the
 * banner at the top of the body.
 */
export function ShiftHandoverFormDialog({
  open,
  handover,
  shifts,
  shiftsFailed,
  canLink,
  pending,
  error,
  onClose,
  onSubmit,
}) {
  const { t } = useTranslation(['app', 'common'])
  const editing = Boolean(handover?.id)

  /* Seeded once per opening — the page mounts this only while it is open. */
  const [form, setForm] = useState(() => seed(handover))
  const set = (key, value) => setForm((previous) => ({ ...previous, [key]: value }))

  const place = useMemo(() => trioOf(form.place), [form.place])

  const fields = error?.fields ?? {}
  const fieldError = (key) => (typeof fields[key] === 'string' ? fields[key] : undefined)
  const placeError = fieldError('plant_id') ?? fieldError('line_id') ?? fieldError('machine_id')
  /* Anything the server said that no control on this form owns. */
  const banner =
    error && !Object.keys(fields).some((key) => KNOWN_FIELDS.has(key))
      ? t('shiftHandovers.form.saveFailed')
      : null

  const valid =
    form.notes.trim().length > 0 &&
    Boolean(place) &&
    Boolean(form.shiftId) &&
    Boolean(form.shiftDate)

  return (
    <ForgeDialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      size="md"
      title={editing ? t('shiftHandovers.form.editTitle', { id: handover.serialNumber }) : t('shiftHandovers.form.title')}
      description={t('shiftHandovers.form.description')}
      footer={
        <div className="flex flex-1 flex-wrap justify-end gap-2">
          <ForgeButton onClick={onClose}>{t('common:actions.cancel')}</ForgeButton>
          <ForgeButton
            variant="primary"
            disabled={!valid || pending}
            onClick={() =>
              onSubmit({
                ...(editing ? { id: handover.id } : { ...place, shiftId: form.shiftId, shiftDate: form.shiftDate }),
                notes: form.notes,
                workOrderIds: idsOf(form.workOrders),
                logbookEntryIds: idsOf(form.logbookEntries),
                anomalyIds: idsOf(form.anomalies),
              })
            }
          >
            {pending ? t('shiftHandovers.form.saving') : editing ? t('shiftHandovers.form.save') : t('shiftHandovers.form.submit')}
          </ForgeButton>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        {banner ? (
          <p role="alert" className="m-0 rounded-lg tint-red px-3 py-2 text-body-sm text-on-red">
            {banner}
          </p>
        ) : null}

        {/* The same drill-down dialog every place picker in the app opens —
            see `PlaceField`. Site, line or asset: `ExactlyOneParentMixin`
            takes any one of the three. */}
        <PlaceField
          label={t('shiftHandovers.form.place')}
          required
          disabled={editing}
          value={form.place}
          onChange={(value) =>
            setForm((previous) => ({
              ...previous,
              place: value,
              /* Links belong to the place that was just replaced, and the
                 server takes only exact-place links — clear them. */
              workOrders: [],
              logbookEntries: [],
              anomalies: [],
            }))
          }
          placeholder={t('shiftHandovers.form.placePlaceholder')}
          hint={editing ? t('shiftHandovers.form.fixedHint') : t('shiftHandovers.form.placeHint')}
          error={placeError}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          {shiftsFailed && !editing ? (
            <p className="m-0 text-body-sm text-on-amber">{t('shiftHandovers.form.shiftsFailed')}</p>
          ) : (
            <ForgeSelect
              label={t('shiftHandovers.form.shift')}
              required
              disabled={editing}
              value={form.shiftId}
              onValueChange={(value) => set('shiftId', value)}
              placeholder={
                shifts.length ? t('shiftHandovers.form.shiftPlaceholder') : t('shiftHandovers.form.noShifts')
              }
              error={fieldError('shift_id')}
              options={shiftOptions(shifts, handover)}
            />
          )}
          <Field
            label={t('shiftHandovers.form.shiftDate')}
            type="date"
            required
            disabled={editing}
            value={form.shiftDate}
            onChange={(event) => set('shiftDate', event.target.value)}
            hint={editing ? undefined : t('shiftHandovers.form.shiftDateHint')}
            error={fieldError('shift_date')}
          />
        </div>

        <TextArea
          rows={6}
          label={t('shiftHandovers.form.notes')}
          required
          maxLength={NOTES_MAX_LENGTH}
          value={form.notes}
          onChange={(event) => set('notes', event.target.value)}
          placeholder={t('shiftHandovers.form.notesPlaceholder')}
          hint={t('shiftHandovers.form.notesHint')}
          error={fieldError('notes')}
        />

        {LINK_KINDS.map(({ kind, wire }) => (
          <LinkField
            key={kind}
            kind={kind}
            place={form.place}
            trio={place}
            max={LINKS_MAX}
            value={form[kind]}
            onChange={(items) => set(kind, items)}
            blocked={
              !place
                ? t('shiftHandovers.form.pickPlaceFirst')
                : !canLink[kind]
                  ? t(`shiftHandovers.form.noGrant.${kind}`)
                  : undefined
            }
            error={fieldError(wire)}
          />
        ))}
      </div>
    </ForgeDialog>
  )
}

const LINK_KINDS = [
  { kind: 'workOrders', wire: 'work_order_ids' },
  { kind: 'logbookEntries', wire: 'logbook_entry_ids' },
  { kind: 'anomalies', wire: 'anomaly_ids' },
]

const idsOf = (items) => items.map((item) => item.id)

/** A linked item as the form holds it — the handover's summaries carry more. */
const linkOf = (item) => ({ id: item.id, serialNumber: item.serialNumber, title: item.title })

const KNOWN_FIELDS = new Set([
  'plant_id',
  'line_id',
  'machine_id',
  'shift_id',
  'shift_date',
  'notes',
  'work_order_ids',
  'logbook_entry_ids',
  'anomaly_ids',
])

function seed(handover) {
  if (!handover?.id) {
    return {
      place: null,
      shiftId: '',
      /* Today, in the reader's zone. A night shift written up after midnight is
         the case this gets wrong, and the date is a field they can correct. */
      shiftDate: todayISO(),
      notes: '',
      workOrders: [],
      logbookEntries: [],
      anomalies: [],
    }
  }
  return {
    place: placeOf(handover),
    shiftId: handover.shiftId ?? '',
    shiftDate: handover.shiftDate ?? '',
    notes: handover.notes ?? '',
    workOrders: (handover.workOrders ?? []).map(linkOf),
    logbookEntries: (handover.logbookEntries ?? []).map(linkOf),
    anomalies: (handover.anomalies ?? []).map(linkOf),
  }
}

function shiftOptions(shifts, handover) {
  const options = shifts.map((shift) => ({
    value: shift.id,
    label: shift.start && shift.end ? `${shift.name} · ${shift.start}–${shift.end}` : shift.name,
  }))
  if (handover?.shiftId && !options.some((option) => option.value === handover.shiftId)) {
    options.unshift({ value: handover.shiftId, label: handover.shift?.name ?? handover.shiftId })
  }
  return options
}
